<template>
  <v-chart class="chart" id="barChartBottomRight" ref="chart" :option="option" autoresize />
</template>

<script setup>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  DatasetComponent,
  GridComponent,
  VisualMapComponent,
  ToolboxComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  GraphicComponent,
} from 'echarts/components';
import VChart, { THEME_KEY } from 'vue-echarts';

import { ref, provide } from 'vue';
import { Lang } from '@/lang/lang';
import { colorList } from '@/config/chartColors';
import { LabelLayout } from 'echarts/features';
import * as echarts from "echarts"; // 别删
import { separator } from '@/utils/format.js';



const limitDataLength = ref(100);
use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  DatasetComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  VisualMapComponent,
  ToolboxComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  GraphicComponent,
  LabelLayout,
]);

provide(THEME_KEY, 'light');
const props = defineProps(['title', 'unit'])
const customLabelLayout = (params) => {
  // 动态布局可以使用 图表的宽度减去label容器的宽度
  let x = document.getElementById('barChartBottomRight').clientWidth - 15 - params.labelRect.width;
  let y = params.rect.y - params.labelRect.height // 单位的位置
  return {
    x: x.toFixed(2),
    y: y.toFixed(2),
  };
};
const baseOption = {
  title: { 
    text: `{titleClass|${props.title || ''}}`,
    left: '20px',
    textStyle: { 
      color: '#192434',
      rich: {
        titleClass: {
          fontSize: 16,
          fontFamily: 'Roboto-Bold',
          padding: [0, 0, 0, 12], // Add padding to separate the border from the text
        },
      },
    }
  },
  graphic: [
    {
      type: 'rect',
      left: '20px',
      top: '3px',
      shape: {
        width: 3,
        height: 20 // 与标题字体大小一致
      },
      style: {
        fill: '#2684FC'
      }
    }
  ],
  // 提示框组件
  tooltip: {
    show: false,
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#2684FC',
        borderColor: '#2684FC'
      }
    },
    backgroundColor: 'rgba(15, 23, 42, 0.9)', // 设置背景颜色为黑色
    textStyle: {
      color: '#fff', // 设置文字颜色为白色
      fontSize: 14
    },
    borderColor: 'transparent', // 设置边框颜色
    borderWidth: 0, // 设置边框宽度
    padding: [10, 15], // 设置内边距
    formatter: function (params) {
      // 获取当前系列的
      const unit = params[0].seriesUnit || props.unit; // 如果没有定义，默认使用
      let result = `${params[0].name}<br/>`; // 第一行显示数据名称

      params.forEach(item => {
        let separatorValue = item.value;
        if (separatorValue !== '') {
          separatorValue = separator(item.value, 3)
        } else {
          separatorValue = '-'
        }
        result += `
          <div style="display: flex; justify-content: space-between;">
              <span style="flex: 1;">${item.marker} ${item.seriesName}</span>
              <span style="text-align: right;padding-left: 12px;font-weight: bold;">${separatorValue} <span style="font-weight: normal;">${unit}</span></span>
          </div>
        `;
      });
      return result;
    }
  },

  // 直角坐标系内绘图网格
  grid: {
    height: '95%',
    left: '15',
    right: '15',
    bottom: 20,
    top: '50',
    containLabel: true
  },
  // X轴
  xAxis: {
    type: 'value',
    // max: 100,
    show: false,
    axisTick: {
      show: false
    },
    nameTextStyle: {
      fontSize: 10,
      color: '#192434',
      align: 'center'
    },

    axisLine: {
      show: false,
      lineStyle: {
        color: '#B3B3B3'
      }
    }
  },
  // Y轴
  yAxis: {
    type: 'category',
    // 轴线设置
    axisLine: {
      show: false,
      lineStyle: {
        // color: "#B3B3B3"
      }
    },
    // 坐标轴刻度相关设置
    axisTick: {
      show: false,
      alignWithLabel: true  // 确保刻度线与标签对齐
    },
    // 坐标轴刻度标签的相关设置
    axisLabel: {
      show: true,
      padding: [0, 0, 40, 0],
      formatter(value, index) {
        // const separatorValue = separator(value, 2)
        // return [`{number|No.${6 - index}} {value|${value}}`];
        return [`{value|${value}}`];
      },
      rich: {
        number: {
          fontSize: 14,
          fontWeight: 400,
          color: '#616D7C',
        },
        value: {
          fontSize: 14,
          color: '#333333'
        }
      },
      interval: 0,
      margin: 0,
      inside: true
    },
    data: []
  },
  series: [
    {
      name: Lang.label.quantity,
      type: 'bar',
      itemStyle: {
        // borderRadius: [0, 10, 10, 0] //圆角位置，左上、右上、右下、左下圆角
      },
      label: {
        show: true,
        distance: -2,
        position: 'top',
        formatter: (params) => {
          // return [`{value|${params.data}} {unit|kWh}`];
          return ''
        },
        rich: {
          value: {
            fontSize: 16,
            fontWeight: 600,
            color: '#2684F6',
          },
          unit: {
            fontSize: 14,
            fontWeight: 400,
            color: '#616D7C',
          }
        }
      },
      labelLayout: customLabelLayout,
      barWidth: 16,
      backgroundStyle: {
        color: '#F3F5FB' // Set background color
      },

      showBackground: true,
      backgroundStyle: {
        color: '#F3F5FB'
      },
      barCategoryGap: '80%',
      data: [0, 0, 0, 0, 0, 0]
    }
  ],
  dataZoom: [
    {
      show: false,
      type: 'slider',  // 使用滑动条类型的数据缩放组件
      orient: 'vertical',  // 设置为垂直方向
      // yAxisIndex: [0, 1],  // 应用于第一个y轴
      // filterMode: 'filter',  // 过滤模式
      start: 0,  // 开始位置（百分比）
      // end: 100,   // 结束位置（百分比）
      zoomLock: true, // 禁止缩放
      // top: '20%',
      handleSize: 0,
      // bottom: 3,
      width: 0,
      // height: '60%',
      showDetail: false,
      handleStyle: {
        color: 'rgba(38, 132, 252, 0.1)' // 设置手柄颜色透明度为50%
      }
    },
    {
      // 没有下面这块的话，只能拖动滚动条，
      // 鼠标滚轮在区域内不能控制外部滚动条
      type: "inside",
      yAxisIndex: [0, 1],
      filterMode: 'empty',
      // zoomLock: true, // 禁止缩放
      // 滚轮是否触发缩放
      zoomOnMouseWheel: false,
      // 鼠标滚轮触发滚动
      moveOnMouseMove: true,
      moveOnMouseWheel: true,
    },
  ],
};

const option = ref(baseOption);

function getSeries(data) {
  const length = data.length;
  const colors = colorList.slice(0, length).reverse();
  let series = [
    {
      name: Lang.label.quantity,
      type: 'bar',
      itemStyle: {
        color: (params) => {
          // 根据数据索引动态设置颜色
          return colors[params.dataIndex];
        },
        // borderRadius: [0, 10, 10, 0] //圆角位置，左上、右上、右下、左下圆角
      },
      label: {
        show: true,
        distance: -2,
        position: 'top',
        formatter: (params) => {
          // if (!data.length) {
          //   return '';
          // }
          const separatorValue = separator(params.data, 3)
          return [`{value|${separatorValue}} {unit|${props.unit}}`];
        },
        rich: {
          value: {
            fontSize: 16,
            fontWeight: 600,
            color: '#2684FC',
            fontFamily: 'D-DIN-Bold',
          },
          unit: {
            fontSize: 14,
            fontWeight: 400,
            padding: [3, 0, 0, 0],
            color: '#616D7C',
          }
        }
      },
      labelLayout: customLabelLayout,
      barWidth: 16,
      backgroundStyle: {
        color: '#F3F5FB' // Set background color
      },
      showBackground: true,
      backgroundStyle: {
        color: '#F3F5FB'
      },
      color: '#2684FC',
      data: []
    }
  ];
  let keys = [];
  let seriesData = [];
  let maxValue = 0;
  let total = 0;
  if (data) {
    data = data.reverse();
    const tempData = [];
    data.forEach((element, i) => {
      total += (element.value - 0)
      if (element.value > maxValue) {
        maxValue = element.value - 0;
      }
      keys.push(element.name);
      seriesData.push(element.value);
      tempData.push(element)
    })
  }
  if (seriesData.length > limitDataLength.value) {
    seriesData = seriesData.splice(0, limitDataLength.value);
  }
  if (keys.length > limitDataLength.value) {
    keys = keys.splice(0, limitDataLength.value);
  }
  series[0].data = seriesData;
  return { series, keys, maxValue, total };
}

function updateChart(data) {
  const { series, keys, maxValue, total} = getSeries(data);
  if (data.length) {
    option.value.xAxis = {
      type: 'value',
    }
    option.value.series = series;
    option.value.yAxis.data = keys;
    option.value.xAxis.max = total;
    console.log('maxValue', maxValue)
    if (keys.length > 5) {
      option.value.dataZoom[0].show = true;
    } else {
      option.value.dataZoom[0].show = false;
    }
    // let end = 100 / ( 58/ 348 * keys.length);
    // option.value.dataZoom[0].end = end;
    // option.value.dataZoom[0].maxValueSpan = 5;
    const visiblePercentage = Math.min(100, (5 / keys.length) * 100); // 可见区域占总数据的百分比
    option.value.dataZoom[0].start = 100 - visiblePercentage; // 起始位置为底部
    option.value.dataZoom[0].end = 100; // 结束位置为底部
  } else {
    // 清空
    option.value.series = [{ data: [0, 0, 0, 0, 0] }];
    option.value.yAxis.data = [];
  }
}
defineExpose({updateChart});
</script>

<style scoped>
/* .chart {
  height: 100vh;
} */
</style>
